import React from "react";
import { createPortal } from "react-dom";
import ChevronLeft24 from "../../../../../../assets/svg/icons/common/ChevronLeft24";
import KebabDots16 from "../../../../../../assets/svg/icons/common/KebabDots16";
import DropDown from "../../../../../../components/DropDown/DropDown";
import Avatar from "../../../../../../components/Avatar/Avatar";
import PhoneIcon from "../../../../../../assets/svg/icons/common/phone";
import MailIcon from "../../../../../../assets/svg/icons/common/mail";
import TrendUp from "../../../../../../assets/svg/icons/common/TrendUp";
import TrendDown from "../../../../../../assets/svg/icons/common/TrendDown";
import Select from "../../../members/member/select";
import HeadingWithLine from "../../../../../../components/HeadingWithLine/HeadingWithLine";
import moment from "moment";
import styles from "./styles.module.scss";

const ROLES = [
  { label: "Admin", value: "admin" },
  { label: "Agent", value: "agent" },
  { label: "Branch head", value: "branch head" },
  { label: "Office head", value: "office head" },
];

const AVAILABILITY = [
  { label: "Enabled", value: 0 },
  { label: "Disabled", value: 1 },
];

const getAvailabilityColor = (status) => (status == 0 ? "#4CAF50" : "#400006");

function SubOrgDetailPortal({
  subOrg,
  onClose,
  onEditSubOrg,
  onAddMember,
  onRemoveMember,
  updateMemberStatus,
}) {
  if (!subOrg) return null;

  const headerKebabOptions = [
    { text: "Edit", type: "normal", onClick: () => onEditSubOrg(subOrg?.id) },
    { text: "Add Member", type: "normal", onClick: () => onAddMember(subOrg?.id) },
  ];

  const volume = subOrg?.mtd_processing_volume
    ? `$${Number(subOrg.mtd_processing_volume).toLocaleString()}`
    : "$0";

  const getTrendIcon = () => {
    if (!subOrg?.mtd_processing_volume || !subOrg?.mtd_volume_trend) return null;
    return subOrg.mtd_volume_trend === "up" ? <TrendUp /> : <TrendDown />;
  };

  const content = (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onClose}
            aria-label="Back"
          >
            <ChevronLeft24 />
          </button>
          <div className={styles.kebab}>
            <DropDown options={headerKebabOptions}>
              <KebabDots16 />
            </DropDown>
          </div>
        </div>
        <h1 className={styles.title}>{subOrg?.name}</h1>
      </header>

      <main className={styles.main}>
        <section className={styles.metrics}>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>MTD Processing Volume</span>
            <div className={styles.metricValue}>
              {volume}
              <span className={styles.trendIcon}>{getTrendIcon()}</span>
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Open Apps</span>
            <div className={styles.metricValue}>
              {subOrg?.open_applications?.count ?? 0}
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Total Merchants</span>
            <div className={styles.metricValue}>
              {subOrg?.member_count ?? 0}
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Idle Apps</span>
            <div className={styles.metricValue}>
              {subOrg?.non_active_accounts?.count ?? 0}
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Idle Apps (%)</span>
            <div className={styles.metricValue}>
              {subOrg?.non_active_accounts?.percentage ?? 0}%
            </div>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Approvals (mon)</span>
            <div className={styles.metricValue}>
              {subOrg?.approved?.count ?? 0}
            </div>
          </div>
        </section>

        <section className={styles.membersSection}>
          <HeadingWithLine text="MEMBERS" margin={false} />
          <div className={styles.membersList}>
            {(subOrg?.members ?? []).map((member) => (
              <div key={member?.id} className={styles.memberCard}>
                <div className={styles.memberCardTop}>
                  <Avatar name={member?.name?.charAt(0)?.toUpperCase()} />
                  <span className={styles.memberName}>{member?.name}</span>
                  <div className={styles.memberKebab}>
                    <DropDown
                      options={[
                        {
                          text: "Remove Member",
                          type: "danger",
                          onClick: () =>
                            onRemoveMember(subOrg?.id, member?.id),
                        },
                      ]}
                    >
                      <KebabDots16 />
                    </DropDown>
                  </div>
                </div>
                <div className={styles.memberContact}>
                  <span>
                    <PhoneIcon /> {member?.phone_direct ?? "-"}
                  </span>
                  <span>
                    <MailIcon /> {member?.email ?? "-"}
                  </span>
                </div>
                <div className={styles.memberFields}>
                  <div className={styles.field}>
                    <label>Role</label>
                    <span>
                      {ROLES.find((r) => r.value == member?.role)?.label ??
                        "-"}
                    </span>
                  </div>
                  <div className={styles.availabilityRow}>
                    <Select
                      label="Availability"
                      value={member?.disabled}
                      options={AVAILABILITY}
                      onChange={(value) =>
                        updateMemberStatus(member, value)
                      }
                      indicatorColor={getAvailabilityColor(member?.disabled)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Member ID</label>
                    <span>{member?.id ?? "-"}</span>
                  </div>
                  <div className={styles.field}>
                    <label>Joining date</label>
                    <span>
                      {member?.created_at
                        ? moment(member.created_at).format("MMM D, YYYY")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );

  return createPortal(content, document.body);
}

export default SubOrgDetailPortal;
